<script>
import { mapActions, mapState } from 'vuex';
import bem from '@/utils/bem';
import SubNavigation from '@/components/ux/subNavigation/SubNavigation.vue';
import HelpCenters from '@/components/helpCenters/Help-centers.vue';
import companyTabMixin from '@/mixins/companyTabMixin';

const b = bem('help-center-page');

export default {
  components: {
    SubNavigation,
    HelpCenters,
  },
  mixins: [companyTabMixin],
  data() {
    return {
      title: 'Help center',
      selectedPage: null,
    };
  },
  methods: {
    b(firstArg, secondArg) {
      return b(firstArg, secondArg);
    },
    ...mapActions({
      getPagesHelpCenterAction: 'helpCenter/getPagesHelpCenter',
      createHintPageHelpCenterAction: 'helpCenter/createHintPageHelpCenter',
      editHintPageHelpCenterAction: 'helpCenter/editHintPageHelpCenter',
      deletePageHelpCenterAction: 'helpCenter/deletePageHelpCenter',
      createCategoriesHelpCenterAction: 'helpCenter/createCategoriesHelpCenter',
      editCategoriesHelpCenterAction: 'helpCenter/editCategoriesHelpCenter',
      deleteCategoriesHelpCenterAction: 'helpCenter/deleteCategoriesHelpCenter',
      changePositionHelpCenterAction: 'helpCenter/changePositionHelpCenter',
      getCompanyPagesAction: 'companyPages/getCompanyPages',
    }),
    async loadCompanyPages() {
      if (this.currentCompany?.id) {
        await this.getCompanyPagesAction(this.currentCompany.id);
      }
    },
    getCurrentPath() {
      if (!this.selectedPage) return '';
      const pageKey = this.currentCompany?.pageKey || '';
      return `pod?pageKey=${pageKey}&pageId=${this.selectedPage.id}`;
    },
    onSelectPage(pageId) {
      this.selectedPage = this.filteredPages.find((p) => p.id === pageId) || null;
      if (this.selectedPage) {
        const path = this.getCurrentPath();
        this.getPagesHelpCenterAction(path);
      }
    },
    createHintPageHelpCenter(body) {
      this.createHintPageHelpCenterAction({
        ...body,
        stepId: 1,
        page: this.getCurrentPath(),
      });
    },
    createCategoriesHelpCenter(body) {
      this.createCategoriesHelpCenterAction({
        ...body,
        page: this.getCurrentPath(),
      });
    },
  },
  computed: {
    ...mapState({
      pageHelpCenter: (state) => state.helpCenter.pageHelpCenter,
      companyPages: (state) => state.companyPages.companyPages,
      isCompanyPagesLoaded: (state) => state.companyPages.isCompanyPagesLoaded,
    }),
    filteredPages() {
      return (this.companyPages || []).filter((page) => !page.isDeleted);
    },
    currentTabTitle() {
      if (!this.selectedPage) return 'Select a page';
      return this.selectedPage.name;
    },
    isLoading() {
      return !this.isCompanyPagesLoaded;
    },
  },
  watch: {
    currentCompany: {
      handler(newVal) {
        if (newVal && newVal.id) {
          this.loadCompanyPages();
        }
      },
      immediate: true,
    },
  },
};
</script>

<template>
  <div :class="b()">
    <a-spin :spinning="!isCurrentCompanyLoaded || isLoading">
      <SubNavigation :company-id="companyId" active-tab="help-center" />

      <div :class="b('content')">
        <div :class="b('headers')">
          <div :class="b('page-select-wrapper')">
            <span :class="b('select-label')">Page:</span>
            <a-select
              :class="b('page-select')"
              :value="selectedPage?.id"
              placeholder="Select a page"
              style="width: 300px"
              @change="onSelectPage"
            >
              <a-select-option
                v-for="page in filteredPages"
                :key="page.id"
                :value="page.id"
              >
                {{ page.name }}
              </a-select-option>
            </a-select>
          </div>
          <h2>Active tab: {{ currentTabTitle }}</h2>
          <div />
        </div>

        <div v-if="!selectedPage" :class="b('empty-state')">
          <p>Please select a page from the dropdown above to manage its help center.</p>
        </div>

        <template v-else>
          <help-centers
            :helpCenters="(pageHelpCenter?.helpCenters || [])"
            :categories="pageHelpCenter?.helpCenters?.filter(item => item.isCategory) || []"
            :currentTab="getCurrentPath()"
            :create-hint="createHintPageHelpCenter"
            :edit-hint="(value) => editHintPageHelpCenterAction(value)"
            :delete-hint="deletePageHelpCenterAction"
            :create-category="createCategoriesHelpCenter"
            :edit-category="(value) => editCategoriesHelpCenterAction(value)"
            :delete-category="deleteCategoriesHelpCenterAction"
            :change-position="changePositionHelpCenterAction"
            :withCreatedLink="false"
          />
        </template>
      </div>
    </a-spin>
  </div>
</template>

<style scoped lang="scss">
@import "@/styles/variables";
@import "@/styles/media";
@import "@/styles/fonts";

.help-center-page {
  &__content {
    padding: 20px;
    background: #fff;
    border-radius: 4px;
    margin: 0 20px;
  }

  &__headers {
    margin: 20px 0;
    display: flex;
    justify-content: space-between;
  }
  &__buttons-menu-item {
    margin-right: 20px;
  }

  &__page-select-wrapper {
    display: flex;
    align-items: center;
    gap: 12px;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(12);
    }
  }

  &__select-label {
    font-family: Montserrat, sans-serif;
    font-size: 16px;
    font-weight: 500;
    color: #293241;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(16);
    }
  }

  &__page-select {
    min-width: 300px;

    @include mediaMinMax(1025, $normal) {
      min-width: vw-full(300);
    }
  }
  &__order-buttons {
    display: flex;
    flex-direction: column;
  }
  &__order-button-up {
    margin-bottom: 10px;
  }

  &__hint-list {
    display: flex;
    flex-direction: column;
    gap: 20px;
    max-width: 1544px;
    margin: 0 auto;
  }

  &__create-hint-button {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__empty-state {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
    font-family: Montserrat, sans-serif;
    font-size: 18px;
    color: #7f7f7f;
    text-align: center;

    @include mediaMinMax(1025, $normal) {
      padding: vw-full(60) vw-full(20);
      font-size: vw-full(18);
    }
  }

  &__step-name {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    font-size: 20px;

    @include mediaMinMax(1025, $normal) {
      padding: vw-full(20);
      font-size: vw-full(20);
    }
  }

  &__buttons {
    margin: 40px auto;
    width: 100%;
    max-width: 1544px;
    display: flex;
    justify-content: space-between;
    align-items: center;

    @include mediaMinMax(1025, $normal) {
      margin: vw-full(40) auto;
      max-width: vw-full(1544);
    }
  }

  &__button {
    width: 170px;
    height: 70px;
    display: flex;
    justify-content: center;
    align-items: center;
    text-decoration: none;
    outline: none;
    border: none;
    box-shadow: 3.8px 5.9px 9.9px 3.1px rgba(95, 138, 164, 0.42);
    background-color: $horizon;
    text-transform: uppercase;
    font-size: 22px;
    font-weight: 500;
    text-align: center;
    color: $white-color;

    @include mediaMinMax(1025, $normal) {
      width: vw-full(170);
      height: vw-full(70);
      font-size: vw-full(22);
    }
    @include mediaMinMax(481, $small) {
      width: vw-mobile(279);
      height: vw-mobile(47);
      font-size: vw-mobile(15);
    }
    @include media($mini) {
      width: 279px;
      height: 47px;
      font-size: 15px;
    }

    &_next {
      margin-left: auto;
    }

    &_back {
      margin-right: auto;
    }
  }
}

button:first-child {
  margin-right: 20px;
}
</style>
